import { useCallback, useContext, useMemo } from "react";

import { SubscriptionStatus, SubscriptionTier } from "../../../api/public";
import { AppContext } from "../../../state/AppContext";

type Mode = "or" | "and";

type Props = {
  feature: string;
  requiredOrgTier?: SubscriptionTier;
  mode?: Mode;
  allowForFreeTrial?: boolean;
  notAllowedSplash?: () => JSX.Element | null;
};

const ORG_TIERS = [
  SubscriptionTier.FREE,
  SubscriptionTier.BASIC,
  SubscriptionTier.PREMIUM_1,
  SubscriptionTier.PREMIUM_2,
  SubscriptionTier.ENTERPRISE,
];

const checkOrgTier = (
  orgTier: SubscriptionTier | undefined,
  requiredOrgTier: SubscriptionTier | undefined
) => {
  if (!requiredOrgTier) return undefined;
  if (!orgTier) return false;

  return ORG_TIERS.indexOf(orgTier) >= ORG_TIERS.indexOf(requiredOrgTier);
};

export const useRestrictedFeatureCheck = (mode: Mode = "or") => {
  const { features, me, activeOrganizationId } = useContext(AppContext);

  const matchingOrg = useMemo(() => {
    if (!activeOrganizationId) return;
    return (me?.organizations ?? []).find(
      (org) => org.id === activeOrganizationId
    );
  }, [activeOrganizationId, me]);

  const checkAccess = useCallback(
    (feature?: string, requiredOrgTier?: SubscriptionTier) => {
      if (!feature && !requiredOrgTier) {
        return true;
      }
      const hasFeature = features.has(feature ?? "");
      const hasSufficientOrgTier =
        checkOrgTier(matchingOrg?.tier, requiredOrgTier) &&
        matchingOrg?.subscription_status === SubscriptionStatus.ACTIVE;
      const allow =
        mode === "or"
          ? !!hasFeature || !!hasSufficientOrgTier
          : !!hasFeature && !!hasSufficientOrgTier;
      return allow;
    },
    [features, matchingOrg?.subscription_status, matchingOrg?.tier, mode]
  );

  return { checkAccess };
};

export const RestrictedFeature = ({
  children,
  feature,
  requiredOrgTier,
  notAllowedSplash,
  allowForFreeTrial = true,
  mode = "or",
}: React.PropsWithChildren<Props>) => {
  const { features, me, activeOrganizationId, isFreeTrial } =
    useContext(AppContext);

  const matchingOrg = useMemo(() => {
    if (!activeOrganizationId) return;
    return (me?.organizations ?? []).find(
      (org) => org.id === activeOrganizationId
    );
  }, [activeOrganizationId, me]);

  const hasFeature = features.has(feature);
  const hasSufficientOrgTier = useMemo(
    () => checkOrgTier(matchingOrg?.tier, requiredOrgTier),
    [matchingOrg?.tier, requiredOrgTier]
  );
  const allow = useMemo(() => {
    if (isFreeTrial && !allowForFreeTrial) {
      return false;
    }

    if (mode === "or") {
      return !!hasFeature || !!hasSufficientOrgTier;
    } else {
      return !!hasFeature && !!hasSufficientOrgTier;
    }
  }, [allowForFreeTrial, hasFeature, hasSufficientOrgTier, isFreeTrial, mode]);

  if (!allow) {
    return notAllowedSplash?.() ?? null;
  }

  return <>{children}</>;
};

export default RestrictedFeature;
